import type { CardCompare, CardMetric } from '@ctd/agent-contract';
import { formatMetricValue } from './card-format';

export type DeltaTone = 'good' | 'bad' | 'neutral';

export interface CardDelta {
    tone: DeltaTone;
    /** Absent when the data has no relative change (a base of zero). */
    relative?: string;
    absolute: string;
}

const signed = (value: number, text: string): string =>
    `${value > 0 ? '+' : value < 0 ? '-' : ''}${text}`;

/** The absolute change as shown, and whether it reads as no change at all once rounded. */
function describeAbsolute(
    unit: CardMetric['unit'],
    abs: number
): { text: string; readsAsNone: boolean } {
    if (unit === 'ratio') {
        const points = Number((abs * 100).toFixed(1));
        return {
            text: signed(points, `${Math.abs(points).toFixed(1)} pts`),
            readsAsNone: points === 0,
        };
    }
    const magnitude = formatMetricValue({ unit, value: Math.abs(abs) });
    const readsAsNone = magnitude === formatMetricValue({ unit, value: 0 });
    return {
        text: readsAsNone ? magnitude : signed(abs, magnitude),
        readsAsNone,
    };
}

function deltaTone(
    abs: number,
    lowerIsBetter: boolean | undefined,
    readsAsNone: boolean
): DeltaTone {
    if (lowerIsBetter === undefined || readsAsNone) {
        return 'neutral';
    }
    const improved = lowerIsBetter ? abs < 0 : abs > 0;
    return improved ? 'good' : 'bad';
}

/** A change as the card shows it: `pct` is a fraction (0.04 is 4%), `abs` is in the metric's unit. */
export function describeDelta(
    { unit, lowerIsBetter }: Pick<CardMetric, 'unit' | 'lowerIsBetter'>,
    { abs, pct }: CardCompare
): CardDelta {
    const percent = pct === null ? null : Number((pct * 100).toFixed(1));
    const absolute = describeAbsolute(unit, abs);
    return {
        tone: deltaTone(abs, lowerIsBetter, absolute.readsAsNone && !percent),
        relative:
            percent === null
                ? undefined
                : signed(percent, `${Math.abs(percent).toFixed(1)}%`),
        absolute: absolute.text,
    };
}
